import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import {
  X, Play, RefreshCw, CheckCircle, AlertCircle, Copy, Terminal, Clock, Edit3,
  Upload, Link, Image, Trash2
} from 'lucide-react';
import { apiService, getApiErrorMessage, MCPAttachmentUploadGrant, MCPToolDefinition, MCPPlaygroundResponse, Index, Video, Prompt, PromptRun, Connector, Webhook, ImportJob } from '../services/api';
import { classifyUploadMedia, exceedsMediaUploadSizeLimit, isSupportedLocalMediaFile, LOCAL_MEDIA_FILE_ACCEPT, LOCAL_VIDEO_FILE_ACCEPT, MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE } from '../utils/mediaUploadPolicy';
import {
  clearPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import {
  buildAsyncRequestOwnerKey,
  isCurrentAsyncRequest,
} from '../utils/asyncRequestOwnership';
import Ajv from 'ajv';
import McpSchemaField, { pruneMcpArguments, resolveMcpSchema } from './McpSchemaField';
import { buildResultLink } from '../navigation/resultLinks';
import CustomSelect from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './McpToolSidebar.css';

interface McpToolSidebarProps {
  tool: MCPToolDefinition | null;
  isOpen: boolean;
  onClose: () => void;
}

// Fields that can have smart dropdowns
const SMART_FIELDS = ['index_id', 'video_id', 'video_ids', 'run_id', 'run_ids', 'prompt_id', 'connector_id', 'job_id', 'webhook_id', 'export_id'];

// Fields that support image upload
const IMAGE_FIELDS = ['image_data'];
const MCP_PLAYGROUND_PENDING_ATTEMPT_NAMESPACE = 'mcp-playground';

interface ImageUploadState {
  mode: 'upload' | 'url';
  preview: string | null;
  fileName: string | null;
  isLoading: boolean;
  error: string | null;
}

// Interface for persisted tool state
interface PersistedToolState {
  playgroundArgs: Record<string, any>;
  result: MCPPlaygroundResponse | null;
  manualMode: Record<string, boolean>;
  helperIndexId: string;
  imageState: ImageUploadState;
  imageUrlInput: string;
  mediaFiles: Array<{ file: File; id: string }>;
}

const DEFAULT_IMAGE_STATE: ImageUploadState = {
  mode: 'upload',
  preview: null,
  fileName: null,
  isLoading: false,
  error: null
};

export const getMcpPlaygroundDisplayPayload = (
  result: MCPPlaygroundResponse | null,
): Record<string, any> | null | undefined => {
  if (!result) {
    return undefined;
  }
  if (!result.success) {
    return result.error;
  }
  const rawResult = result.result;
  if (
    rawResult
    && typeof rawResult === 'object'
    && rawResult.structuredContent
    && typeof rawResult.structuredContent === 'object'
  ) {
    return rawResult.structuredContent as Record<string, any>;
  }
  return rawResult;
};

export const getMcpTaskReadRequest = (response: MCPPlaygroundResponse) => {
  const value = getMcpPlaygroundDisplayPayload(response);
  if (typeof value?.analysis_id === 'string') return { name: 'get_analysis', arguments: { analysis_id: value.analysis_id, wait_seconds: 0 } };
  if (typeof value?.import_id === 'string') return { name: 'import_media', arguments: { import_id: value.import_id, wait_seconds: 0 } };
  return null;
};

export const getMcpUploadAcceptedResult = (response: MCPPlaygroundResponse): MCPPlaygroundResponse => {
  const value = { ...getMcpPlaygroundDisplayPayload(response), status: 'unknown', stage: 'status_unavailable',
    message: 'Files uploaded. Read this same task to refresh its progress.', next_action: { type: 'continue_polling', after_seconds: 15 } };
  const metadata = { ...response.result?._meta };
  delete metadata.upload;
  if (metadata.analysis) metadata.analysis = value;
  if (metadata.import) metadata.import = value;
  return { ...response, result: { ...response.result, structuredContent: value,
    content: [{ type: 'text', text: JSON.stringify(value) }], _meta: metadata } };
};

export const getMcpAnalysisResultLink = (value: Record<string, any> | null | undefined): string | null => {
  const videoId = value?.results?.videos?.[0]?.video_id;
  if (!value?.analysis_id || !videoId) return null;
  return buildResultLink({ videoId, runId: value.analysis_id, indexId: value.scope?.index_id ?? null,
    segmentId: null, output: null, playback: null, metadata: null, resultScope: 'all', time: null, field: null });
};

export const shouldRetainMcpPlaygroundAttemptKey = (
  response: MCPPlaygroundResponse,
): boolean => !response.success;

export const getMcpPlaygroundAttemptPayload = (
  tool: MCPToolDefinition,
  args: Record<string, any>,
): Record<string, any> => {
  const requestPayload = { ...args };
  delete requestPayload.idempotency_key;
  Object.entries(tool.inputSchema.properties || {}).forEach(([name, schema]) => {
    if (
      requestPayload[name] === undefined
      && schema
      && Object.prototype.hasOwnProperty.call(schema, 'default')
    ) {
      requestPayload[name] = schema.default;
    }
  });
  return requestPayload;
};

export const shouldPublishMcpPlaygroundExecutionResult = (
  visibleToolName: string | null,
  executionToolName: string,
): boolean => visibleToolName === executionToolName;

const McpToolSidebar: React.FC<McpToolSidebarProps> = ({
  tool,
  isOpen,
  onClose
}) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('mcpToolSidebarWidth');
    return saved ? parseInt(saved, 10) : 600;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [playgroundArgs, setPlaygroundArgs] = useState<Record<string, any>>({});
  const [result, setResult] = useState<MCPPlaygroundResponse | null>(null);
  const [resultCopied, setResultCopied] = useState(false);

  // Smart field data
  const [indexes, setIndexes] = useState<Index[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptRuns, setPromptRuns] = useState<PromptRun[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [loadingIndexes, setLoadingIndexes] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingPromptRuns, setLoadingPromptRuns] = useState(false);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [loadingImportJobs, setLoadingImportJobs] = useState(false);

  // Manual entry mode for smart fields
  const [manualMode, setManualMode] = useState<Record<string, boolean>>({});

  // Helper index for tools that need run_id but don't have index_id as parameter
  const [helperIndexId, setHelperIndexId] = useState<string>('');

  // Image upload state
  const [imageState, setImageState] = useState<ImageUploadState>(DEFAULT_IMAGE_STATE);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [mediaFiles, setMediaFiles] = useState<PersistedToolState['mediaFiles']>([]);
  const [schemaErrors, setSchemaErrors] = useState<Record<string, string>>({});
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Persistence: store state for each tool
  const toolStatesRef = useRef<Record<string, PersistedToolState>>({});
  const playgroundAttemptKeysRef = useRef<Record<string, PendingIdempotencyAttempt>>({});
  const executionInFlightRef = useRef(false);
  const imageRequestGenerationRef = useRef(0);
  const previousToolRef = useRef<string | null>(null);
  const visibleToolNameRef = useRef<string | null>(null);
  visibleToolNameRef.current = isOpen && tool ? tool.name : null;

  // Refs to access current state values without triggering re-renders
  const currentStateRef = useRef({
    playgroundArgs,
    result,
    manualMode,
    helperIndexId,
    imageState,
    imageUrlInput,
    mediaFiles,
  });

  // Keep refs in sync with state
  useLayoutEffect(() => {
    currentStateRef.current = {
      playgroundArgs,
      result,
      manualMode,
      helperIndexId,
      imageState,
      imageUrlInput,
      mediaFiles,
    };
  }, [playgroundArgs, result, manualMode, helperIndexId, imageState, imageUrlInput, mediaFiles]);

  // Save current tool state and restore new tool state when tool changes
  useLayoutEffect(() => {
    const currentToolName = isOpen && tool ? tool.name : null;
    const previousToolName = previousToolRef.current;
    if (previousToolName !== currentToolName) {
      imageRequestGenerationRef.current += 1;
    }

    // Save state of previous tool (if exists)
    if (previousToolName && previousToolName !== currentToolName) {
      toolStatesRef.current[previousToolName] = {
        playgroundArgs: currentStateRef.current.playgroundArgs,
        result: currentStateRef.current.result,
        manualMode: currentStateRef.current.manualMode,
        helperIndexId: currentStateRef.current.helperIndexId,
        imageState: {
          ...currentStateRef.current.imageState,
          isLoading: false,
        },
        imageUrlInput: currentStateRef.current.imageUrlInput,
        mediaFiles: currentStateRef.current.mediaFiles,
      };
    }

    if (!currentToolName || !tool) {
      previousToolRef.current = null;
      return;
    }
    setMediaError(null);
    setSchemaErrors({});

    // Restore state for current tool (if exists) or initialize fresh
    const savedState = toolStatesRef.current[currentToolName];
    if (savedState) {
      setPlaygroundArgs(savedState.playgroundArgs);
      setResult(savedState.result);
      setManualMode(savedState.manualMode);
      setHelperIndexId(savedState.helperIndexId);
      setImageState(savedState.imageState);
      setImageUrlInput(savedState.imageUrlInput);
      setMediaFiles(savedState.mediaFiles);
    } else {
      // Initialize fresh state for new tool
      setPlaygroundArgs({});
      setResult(null);
      setManualMode({});
      setHelperIndexId('');
      setImageState(DEFAULT_IMAGE_STATE);
      setImageUrlInput('');
      setMediaFiles([]);
    }

    // Clear dependent data that needs to reload based on new tool's index_id
    setVideos([]);
    setPromptRuns([]);

    // Update previous tool reference
    previousToolRef.current = currentToolName;
  }, [isOpen, tool]);

  // Load indexes, prompts, connectors, webhooks when sidebar opens
  useEffect(() => {
    if (isOpen && tool) {
      const fields = tool.inputSchema.properties || {};
      if (['index_id', 'video_id', 'video_ids', 'run_id', 'run_ids'].some(key => key in fields)) loadIndexes();
      if ('prompt_id' in fields) loadPrompts();
      if ('connector_id' in fields) loadConnectors();
      if ('webhook_id' in fields) loadWebhooks();
      if ('job_id' in fields) loadImportJobs();
    }
  }, [isOpen, tool]);

  const selectedIndexId = typeof playgroundArgs['index_id'] === 'string'
    ? playgroundArgs['index_id']
    : '';
  const videoManualModeEnabled = Boolean(manualMode['video_id'] || manualMode['video_ids']);
  const runManualModeEnabled = Boolean(manualMode['run_id'] || manualMode['run_ids']);

  // Load videos and prompt runs when index_id changes (for tools with index_id param)
  useEffect(() => {
    if (selectedIndexId && !videoManualModeEnabled) {
      loadVideos(selectedIndexId);
    }
    if (selectedIndexId && !runManualModeEnabled) {
      loadPromptRuns(selectedIndexId);
    }
  }, [runManualModeEnabled, selectedIndexId, videoManualModeEnabled]);

  // Load videos and prompt runs when helper index changes (for tools without index_id param)
  useEffect(() => {
    if (helperIndexId) {
      if (!videoManualModeEnabled) {
        loadVideos(helperIndexId);
      }
      if (!runManualModeEnabled) {
        loadPromptRuns(helperIndexId);
      }
    }
  }, [helperIndexId, runManualModeEnabled, videoManualModeEnabled]);

  const loadIndexes = async () => {
    setLoadingIndexes(true);
    try {
      const data = await apiService.getIndexes();
      setIndexes(data);
    } catch (error) {
      console.error('Failed to load indexes:', error);
    } finally {
      setLoadingIndexes(false);
    }
  };

  const loadVideos = async (indexId: string) => {
    setLoadingVideos(true);
    try {
      const response = await apiService.getVideosInIndex(
        indexId,
        100,
        undefined,
        { view: 'gallery' },
      );
      setVideos(response.data || []);
    } catch (error) {
      console.error('Failed to load videos:', error);
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  const loadPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const response = await apiService.getPrompts(true, true);
      setPrompts(response.prompts || []);
    } catch (error) {
      console.error('Failed to load prompts:', error);
      setPrompts([]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const loadPromptRuns = async (indexId: string) => {
    setLoadingPromptRuns(true);
    try {
      const runs = await apiService.getPromptRunsByIndex(indexId);
      setPromptRuns(runs || []);
    } catch (error) {
      console.error('Failed to load prompt runs:', error);
      setPromptRuns([]);
    } finally {
      setLoadingPromptRuns(false);
    }
  };

  const loadConnectors = async () => {
    setLoadingConnectors(true);
    try {
      const data = await apiService.getConnectors();
      setConnectors(data || []);
    } catch (error) {
      console.error('Failed to load connectors:', error);
      setConnectors([]);
    } finally {
      setLoadingConnectors(false);
    }
  };

  const loadWebhooks = async () => {
    setLoadingWebhooks(true);
    try {
      const data = await apiService.getWebhooks();
      setWebhooks(data || []);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
      setWebhooks([]);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const loadImportJobs = async () => {
    setLoadingImportJobs(true);
    try {
      const data = await apiService.getImportJobs();
      setImportJobs(data || []);
    } catch (error) {
      console.error('Failed to load import jobs:', error);
      setImportJobs([]);
    } finally {
      setLoadingImportJobs(false);
    }
  };

  // Keep argument ownership stable for image callbacks and invalidate any
  // pending idempotency attempt whenever the visible request changes.
  const handleArgChange = useCallback((key: string, value: any) => {
    if (executionInFlightRef.current) {
      return;
    }
    if (tool) {
      delete playgroundAttemptKeysRef.current[tool.name];
    }
    setResult(null);
    setMediaError(null);
    setPlaygroundArgs(prev => pruneMcpArguments(tool?.inputSchema ?? {}, { ...prev, [key]: value }));

    // Clear video_id if index_id changes.
    if (key === 'index_id') {
      setPlaygroundArgs(prev => ({
        ...prev,
        [key]: value,
        video_id: undefined
      }));
      setVideos([]);
    }
  }, [tool]);

  const handleMediaFilesChange = (files: PersistedToolState['mediaFiles']) => {
    if (executionInFlightRef.current || !tool) return;
    // The first selection fills an empty picker; editing an existing batch
    // starts a new invocation instead of reusing its registered file manifest.
    const fillsEmptyPicker = mediaFiles.length === 0 && Boolean(result?.result?._meta?.upload);
    if (!fillsEmptyPicker) {
      const attempt = playgroundAttemptKeysRef.current[tool.name];
      if (attempt) {
        clearPendingIdempotencyAttempt({
          namespace: MCP_PLAYGROUND_PENDING_ATTEMPT_NAMESPACE,
          operation: tool.name,
          requestFingerprint: attempt.requestFingerprint,
          expectedIdempotencyKey: attempt.idempotencyKey,
        });
      }
      delete playgroundAttemptKeysRef.current[tool.name];
      setResult(null);
      if (result || attempt) {
        setPlaygroundArgs(current => ({ ...current, idempotency_key: undefined }));
      }
    }
    setMediaError(null);
    setMediaFiles(files);
  };

  // Convert file to base64 (without data URL prefix)
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Fetch image from URL and convert to base64
  const urlToBase64 = useCallback(async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    const ownerToolName = visibleToolNameRef.current;
    if (!ownerToolName) {
      return;
    }
    const token = {
      generation: imageRequestGenerationRef.current + 1,
      ownerKey: buildAsyncRequestOwnerKey(ownerToolName, 'image'),
    };
    imageRequestGenerationRef.current = token.generation;
    const ownsRequest = () => isCurrentAsyncRequest(
      imageRequestGenerationRef.current,
      buildAsyncRequestOwnerKey(visibleToolNameRef.current, 'image'),
      token,
    );

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setImageState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Invalid file type. Please upload JPEG, PNG, or WebP images.'
      }));
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageState(prev => ({
        ...prev,
        isLoading: false,
        error: 'File too large. Maximum size is 10MB.'
      }));
      return;
    }

    setImageState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const base64 = await fileToBase64(file);
      if (!ownsRequest()) {
        return;
      }
      const previewUrl = URL.createObjectURL(file);

      setImageState(prev => ({
        ...prev,
        preview: previewUrl,
        fileName: file.name,
        isLoading: false,
        error: null
      }));

      handleArgChange('image_data', base64);
    } catch (error) {
      if (ownsRequest()) {
        console.error('Failed to process file:', error);
        setImageState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to process image file.'
        }));
      }
    }
  }, [fileToBase64, handleArgChange]);

  // Handle URL input
  const handleUrlSubmit = useCallback(async (url: string) => {
    if (!url.trim()) return;
    const ownerToolName = visibleToolNameRef.current;
    if (!ownerToolName) {
      return;
    }
    const token = {
      generation: imageRequestGenerationRef.current + 1,
      ownerKey: buildAsyncRequestOwnerKey(ownerToolName, 'image'),
    };
    imageRequestGenerationRef.current = token.generation;
    const ownsRequest = () => isCurrentAsyncRequest(
      imageRequestGenerationRef.current,
      buildAsyncRequestOwnerKey(visibleToolNameRef.current, 'image'),
      token,
    );

    setImageState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const base64 = await urlToBase64(url);
      if (!ownsRequest()) {
        return;
      }

      setImageState(prev => ({
        ...prev,
        preview: url,
        fileName: url.split('/').pop() || 'image',
        isLoading: false,
        error: null
      }));

      setImageUrlInput('');
      handleArgChange('image_data', base64);
    } catch (error) {
      if (ownsRequest()) {
        console.error('Failed to fetch image from URL:', error);
        setImageState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to fetch image from URL. Make sure the URL is accessible and points to an image.'
        }));
      }
    }
  }, [handleArgChange, urlToBase64]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  // Clear image
  const handleClearImage = useCallback(() => {
    imageRequestGenerationRef.current += 1;
    if (imageState.preview && imageState.mode === 'upload') {
      URL.revokeObjectURL(imageState.preview);
    }
    setImageState({
      mode: imageState.mode,
      preview: null,
      fileName: null,
      isLoading: false,
      error: null
    });
    setImageUrlInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    handleArgChange('image_data', undefined);
  }, [handleArgChange, imageState.preview, imageState.mode]);

  const handleImageModeChange = useCallback((mode: ImageUploadState['mode']) => {
    imageRequestGenerationRef.current += 1;
    setImageState(prev => ({
      ...prev,
      mode,
      isLoading: false,
      error: null,
    }));
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && isActive) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isActive, isOpen, onClose]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('mcpToolSidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('mcpToolSidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  // Notify when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      window.dispatchEvent(new CustomEvent('mcpToolSidebarWidthChange', { detail: { width: 0 } }));
    }
  }, [isOpen]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-mcp-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(900, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-mcp-sidebar');
    };
  }, [isActive, isOpen, isResizing]);

  // Toggle manual mode for a field
  const toggleManualMode = (key: string) => {
    setManualMode(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    // Clear the field value when switching modes
    handleArgChange(key, undefined);
  };

  // Execute tool
  const handleExecute = async () => {
    if (!tool || executionInFlightRef.current) return;
    if (Object.keys(schemaErrors).length) return;
    const validate = new Ajv({ strict: false, allErrors: true, validateFormats: false }).compile(tool.inputSchema);
    if (!validate(playgroundArgs)) {
      setMediaError(validate.errors?.map(error => `${error.instancePath || 'Parameters'} ${error.message}`).join('; ') || 'Check the tool parameters.');
      return;
    }
    const executionTool = tool;
    const executionToolName = executionTool.name;
    const executionArgs = { ...playgroundArgs };
    const executionFiles = [...mediaFiles];
    let uploadResponse = result?.result?._meta?.upload ? result : undefined;
    const executionState: PersistedToolState = {
      ...currentStateRef.current,
      playgroundArgs: executionArgs,
      result: null,
    };
    const publishExecutionResult = (response: MCPPlaygroundResponse) => {
      if (shouldPublishMcpPlaygroundExecutionResult(
        visibleToolNameRef.current,
        executionToolName,
      )) {
        setResult(response);
        return;
      }
      const savedState = toolStatesRef.current[executionToolName] || executionState;
      toolStatesRef.current[executionToolName] = {
        ...savedState,
        playgroundArgs: executionArgs,
        result: response,
      };
    };
    executionInFlightRef.current = true;
    setIsExecuting(true);
    setMediaError(null);
    if (!uploadResponse) setResult(null);
    try {
      const explicitArgumentKey = typeof executionArgs.idempotency_key === 'string'
        ? executionArgs.idempotency_key.trim()
        : '';
      const attempt = executionTool.idempotencyProtected
        ? await getOrCreatePendingIdempotencyAttempt({
          namespace: MCP_PLAYGROUND_PENDING_ATTEMPT_NAMESPACE,
          operation: executionToolName,
          requestPayload: getMcpPlaygroundAttemptPayload(
            executionTool,
            executionArgs,
          ),
          keyPrefix: `mcp-playground:${executionToolName}`,
          preferredIdempotencyKey: explicitArgumentKey || undefined,
          inMemoryAttempt: playgroundAttemptKeysRef.current[executionToolName],
        })
        : undefined;
      if (attempt) {
        playgroundAttemptKeysRef.current[executionToolName] = attempt;
      }
      const response = uploadResponse || await apiService.executeMcpPlayground(
        executionToolName,
        executionArgs,
        attempt?.idempotencyKey,
      );
      if (attempt && !shouldRetainMcpPlaygroundAttemptKey(response)) {
        clearPendingIdempotencyAttempt({
          namespace: MCP_PLAYGROUND_PENDING_ATTEMPT_NAMESPACE,
          operation: executionToolName,
          requestFingerprint: attempt.requestFingerprint,
          expectedIdempotencyKey: attempt.idempotencyKey,
        });
        delete playgroundAttemptKeysRef.current[executionToolName];
      }
      publishExecutionResult(response);
      const uploadGrant = response.result?._meta?.upload as MCPAttachmentUploadGrant | undefined;
      if (response.success && uploadGrant && executionFiles.length) {
        uploadResponse = response;
        if (executionFiles.length > uploadGrant.max_files) {
          throw new Error(`Choose up to ${uploadGrant.max_files} media files.`);
        }
        for (let index = 0; index < executionFiles.length; index += 1) {
          const entry = executionFiles[index];
          setUploadProgress(`Uploading ${index + 1} of ${executionFiles.length}: ${entry.file.name}`);
          await apiService.uploadMcpAttachment(uploadGrant, entry.file, entry.id);
        }
        setUploadProgress('Starting workflow…');
        await apiService.completeMcpAttachments(uploadGrant, executionFiles.map(entry => entry.id));
        const accepted = getMcpUploadAcceptedResult(response);
        // Finalization consumed the grant. A failed status read must retain the
        // same task rather than restoring the picker and starting another upload.
        uploadResponse = undefined;
        publishExecutionResult(accepted);
        if (visibleToolNameRef.current === executionToolName) {
          setMediaFiles([]);
        } else if (toolStatesRef.current[executionToolName]) {
          toolStatesRef.current[executionToolName].mediaFiles = [];
        }
        const read = getMcpTaskReadRequest(accepted);
        if (read) {
          try {
            const refreshed = await apiService.executeMcpPlayground(read.name, read.arguments);
            if (refreshed.success) publishExecutionResult(refreshed);
            else if (visibleToolNameRef.current === executionToolName) setMediaError('Files uploaded. Status refresh failed; check this same task again.');
          } catch (error) {
            if (visibleToolNameRef.current === executionToolName) setMediaError(getApiErrorMessage(error, 'Files uploaded. Check this same task again to refresh progress.'));
          }
        }
      }
    } catch (err: any) {
      if (uploadResponse) {
        publishExecutionResult(uploadResponse);
        if (visibleToolNameRef.current === executionToolName) {
          setMediaError(getApiErrorMessage(err, 'Upload failed. Retry to continue the same workflow.'));
        }
      } else publishExecutionResult({
        tool_name: executionToolName,
        success: false,
        error: {
          code: err.response?.data?.error?.code || err.code || 'execution_error',
          message: err.response?.data?.error?.message || err.message || 'Execution failed',
          details: {}
        },
        execution_time_ms: 0
      });
    } finally {
      executionInFlightRef.current = false;
      setIsExecuting(false);
      setUploadProgress('');
    }
  };

  // Copy result to clipboard
  const handleCopyResult = async () => {
    if (!result) return;
    try {
      const displayPayload = getMcpPlaygroundDisplayPayload(result);
      const content = JSON.stringify(
        displayPayload ?? null,
        null,
        2
      );
      await navigator.clipboard.writeText(content);
      setResultCopied(true);
      setTimeout(() => setResultCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Render image upload field
  const renderImageField = (key: string, schema: any, isRequired: boolean) => {
    return (
      <div className="image-upload-field">
        {/* Mode Toggle */}
        <div className="smart-field-header">
          <button
            className={`mode-toggle ${imageState.mode === 'upload' ? 'active' : ''}`}
            onClick={() => handleImageModeChange('upload')}
          >
            <Upload size={12} />
            Upload
          </button>
          <button
            className={`mode-toggle ${imageState.mode === 'url' ? 'active' : ''}`}
            onClick={() => handleImageModeChange('url')}
          >
            <Link size={12} />
            URL
          </button>
        </div>

        {/* Upload Mode */}
        {imageState.mode === 'upload' && !imageState.preview && (
          <div
            className="image-dropzone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
              style={{ display: 'none' }}
            />
            {imageState.isLoading ? (
              <div className="dropzone-loading">
                <RefreshCw size={24} className="spinning" />
                <span>Processing...</span>
              </div>
            ) : (
              <>
                <Image size={32} />
                <span className="dropzone-text">
                  Drag & drop an image here, or click to select
                </span>
                <span className="dropzone-hint">
                  Supports JPEG, PNG, WebP (max 10MB)
                </span>
              </>
            )}
          </div>
        )}

        {/* URL Mode */}
        {imageState.mode === 'url' && !imageState.preview && (
          <div className="image-url-input">
            <input
              type="text"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              placeholder="Paste image URL..."
              className="form-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUrlSubmit(imageUrlInput);
                }
              }}
            />
            <button
              className="url-submit-btn"
              onClick={() => handleUrlSubmit(imageUrlInput)}
              disabled={!imageUrlInput.trim() || imageState.isLoading}
            >
              {imageState.isLoading ? (
                <RefreshCw size={16} className="spinning" />
              ) : (
                'Load'
              )}
            </button>
          </div>
        )}

        {/* Image Preview */}
        {imageState.preview && (
          <div className="image-preview-container">
            <img
              src={imageState.preview}
              alt="Preview"
              className="image-preview"
            />
            <div className="image-preview-info">
              <span className="image-filename">{imageState.fileName}</span>
              <button
                className="clear-image-btn"
                onClick={handleClearImage}
                title="Remove image"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {imageState.error && (
          <div className="image-error">
            <AlertCircle size={14} />
            <span>{imageState.error}</span>
          </div>
        )}

        {/* Hidden status for base64 */}
        {playgroundArgs[key] && !imageState.preview && (
          <div className="image-loaded-hint">
            <CheckCircle size={14} />
            <span>Image data loaded</span>
          </div>
        )}
      </div>
    );
  };

  // Render smart field with dropdown + manual toggle
  const renderSmartField = (key: string, schema: any, isRequired: boolean) => {
    const isManual = manualMode[key];

    // Index field
    if (key === 'index_id') {
      const indexOptions = indexes.map(index => ({
        value: index.index_id,
        label: index.name
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter index ID"
              className="form-input"
            />
          ) : (
            <CustomSelect
              value={playgroundArgs[key] || ''}
              onChange={(value) => handleArgChange(key, value || undefined)}
              options={indexOptions}
              placeholder={loadingIndexes ? 'Loading...' : 'Select an index'}
              disabled={loadingIndexes}
            />
          )}
        </div>
      );
    }

    // Video field
    if (key === 'video_id') {
      // Check if this tool has index_id as a parameter
      const toolHasIndexId = tool?.inputSchema?.properties?.['index_id'] !== undefined;
      const effectiveIndexId = toolHasIndexId ? playgroundArgs['index_id'] : helperIndexId;

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter video ID"
              className="form-input"
            />
          ) : (
            <>
              {toolHasIndexId && !effectiveIndexId && !manualMode['index_id'] && (
                <p className="field-note">Select an index first to see videos</p>
              )}
              {!toolHasIndexId && !effectiveIndexId && (
                <p className="field-note">Use the helper above to select an index</p>
              )}
              <CustomSelect
                value={playgroundArgs[key] || ''}
                onChange={(value) => handleArgChange(key, value || undefined)}
                options={videos.map(video => ({
                  value: video.video_id,
                  label: video.title || video.video_id
                }))}
                placeholder={
                  loadingVideos ? 'Loading...' :
                  videos.length === 0 ? 'No videos available' : 'Select a video'
                }
                disabled={loadingVideos || !effectiveIndexId}
              />
            </>
          )}
        </div>
      );
    }

    // Prompt field
    if (key === 'prompt_id') {
      const promptOptions = prompts.map(prompt => ({
        value: prompt.prompt_id,
        label: prompt.name
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter prompt ID"
              className="form-input"
            />
          ) : (
            <CustomSelect
              value={playgroundArgs[key] || ''}
              onChange={(value) => handleArgChange(key, value || undefined)}
              options={promptOptions}
              placeholder={loadingPrompts ? 'Loading...' : 'Select a prompt'}
              disabled={loadingPrompts}
            />
          )}
        </div>
      );
    }

    // Video IDs field (multi-select)
    if (key === 'video_ids') {
      // Check if this tool has index_id as a parameter
      const toolHasIndexId = tool?.inputSchema?.properties?.['index_id'] !== undefined;
      const effectiveIndexId = toolHasIndexId ? playgroundArgs['index_id'] : helperIndexId;
      const selectedVideoIds = playgroundArgs[key] || [];

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={selectedVideoIds.join(', ')}
              onChange={(e) => handleArgChange(key, e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(s => s) : undefined)}
              placeholder="Enter video IDs (comma-separated)"
              className="form-input"
            />
          ) : (
            <>
              {toolHasIndexId && !effectiveIndexId && !manualMode['index_id'] && (
                <p className="field-note">Select an index first to see videos</p>
              )}
              {!toolHasIndexId && !effectiveIndexId && (
                <p className="field-note">Use the helper above to select an index</p>
              )}
              <div className="multi-select-container">
                {videos.length > 0 && (
                  <div className="multi-select-actions">
                    <button
                      type="button"
                      className="multi-select-action-btn"
                      onClick={() => handleArgChange(key, videos.map(v => v.video_id))}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="multi-select-action-btn"
                      onClick={() => handleArgChange(key, undefined)}
                    >
                      Clear All
                    </button>
                    <span className="multi-select-count">
                      {selectedVideoIds.length} of {videos.length} selected
                    </span>
                  </div>
                )}
                <div className="multi-select-options">
                  {loadingVideos ? (
                    <p className="field-note">Loading videos...</p>
                  ) : videos.length === 0 ? (
                    <p className="field-note">No videos available</p>
                  ) : (
                    videos.map(video => (
                      <label key={video.video_id} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={selectedVideoIds.includes(video.video_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleArgChange(key, [...selectedVideoIds, video.video_id]);
                            } else {
                              handleArgChange(key, selectedVideoIds.filter((id: string) => id !== video.video_id));
                            }
                          }}
                        />
                        <span className="multi-select-label">{video.title || video.video_id}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Run ID field (single select)
    if (key === 'run_id') {
      // Check if this tool has index_id as a parameter
      const toolHasIndexId = tool?.inputSchema?.properties?.['index_id'] !== undefined;
      const effectiveIndexId = toolHasIndexId ? playgroundArgs['index_id'] : helperIndexId;
      const runOptions = promptRuns.map(run => ({
        value: run.run_id,
        label: `${run.prompt_name} - ${run.status} (${new Date(run.executed_at).toLocaleDateString()})`
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter run ID"
              className="form-input"
            />
          ) : (
            <>
              {toolHasIndexId && !effectiveIndexId && !manualMode['index_id'] && (
                <p className="field-note">Select an index first to see prompt runs</p>
              )}
              {!toolHasIndexId && !effectiveIndexId && (
                <p className="field-note">Use the helper above to select an index</p>
              )}
              <CustomSelect
                value={playgroundArgs[key] || ''}
                onChange={(value) => handleArgChange(key, value || undefined)}
                options={runOptions}
                placeholder={
                  loadingPromptRuns ? 'Loading...' :
                  promptRuns.length === 0 ? 'No prompt runs available' : 'Select a prompt run'
                }
                disabled={loadingPromptRuns || !effectiveIndexId}
              />
            </>
          )}
        </div>
      );
    }

    // Run IDs field (multi-select)
    if (key === 'run_ids') {
      // Check if this tool has index_id as a parameter
      const toolHasIndexId = tool?.inputSchema?.properties?.['index_id'] !== undefined;
      const effectiveIndexId = toolHasIndexId ? playgroundArgs['index_id'] : helperIndexId;
      const selectedRunIds = playgroundArgs[key] || [];

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={selectedRunIds.join(', ')}
              onChange={(e) => handleArgChange(key, e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(s => s) : undefined)}
              placeholder="Enter run IDs (comma-separated)"
              className="form-input"
            />
          ) : (
            <>
              {toolHasIndexId && !effectiveIndexId && !manualMode['index_id'] && (
                <p className="field-note">Select an index first to see prompt runs</p>
              )}
              {!toolHasIndexId && !effectiveIndexId && (
                <p className="field-note">Use the helper above to select an index</p>
              )}
              <div className="multi-select-container">
                {promptRuns.length > 0 && (
                  <div className="multi-select-actions">
                    <button
                      type="button"
                      className="multi-select-action-btn"
                      onClick={() => handleArgChange(key, promptRuns.map(r => r.run_id))}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="multi-select-action-btn"
                      onClick={() => handleArgChange(key, undefined)}
                    >
                      Clear All
                    </button>
                    <span className="multi-select-count">
                      {selectedRunIds.length} of {promptRuns.length} selected
                    </span>
                  </div>
                )}
                <div className="multi-select-options">
                  {loadingPromptRuns ? (
                    <p className="field-note">Loading prompt runs...</p>
                  ) : promptRuns.length === 0 ? (
                    <p className="field-note">No prompt runs available</p>
                  ) : (
                    promptRuns.map(run => (
                      <label key={run.run_id} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={selectedRunIds.includes(run.run_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleArgChange(key, [...selectedRunIds, run.run_id]);
                            } else {
                              handleArgChange(key, selectedRunIds.filter((id: string) => id !== run.run_id));
                            }
                          }}
                        />
                        <span className="multi-select-label">
                          {run.prompt_name} - {run.status} ({new Date(run.executed_at).toLocaleDateString()})
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Connector ID field
    if (key === 'connector_id') {
      const connectorOptions = connectors.map(connector => ({
        value: connector.connector_id,
        label: `${connector.name} (${connector.provider})`
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter connector ID"
              className="form-input"
            />
          ) : (
            <CustomSelect
              value={playgroundArgs[key] || ''}
              onChange={(value) => handleArgChange(key, value || undefined)}
              options={connectorOptions}
              placeholder={loadingConnectors ? 'Loading...' : connectors.length === 0 ? 'No connectors available' : 'Select a connector'}
              disabled={loadingConnectors}
            />
          )}
        </div>
      );
    }

    // Webhook ID field
    if (key === 'webhook_id') {
      const webhookOptions = webhooks.map(webhook => ({
        value: webhook.webhook_id,
        label: `${webhook.name} (${webhook.status})`
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter webhook ID"
              className="form-input"
            />
          ) : (
            <CustomSelect
              value={playgroundArgs[key] || ''}
              onChange={(value) => handleArgChange(key, value || undefined)}
              options={webhookOptions}
              placeholder={loadingWebhooks ? 'Loading...' : webhooks.length === 0 ? 'No webhooks available' : 'Select a webhook'}
              disabled={loadingWebhooks}
            />
          )}
        </div>
      );
    }

    // Import Job ID field
    if (key === 'job_id') {
      const jobOptions = importJobs.map(job => ({
        value: job.job_id,
        label: `${job.status} - ${job.file_pattern} (${new Date(job.created_at || '').toLocaleDateString()})`
      }));

      return (
        <div className="smart-field">
          <div className="smart-field-header">
            <button
              className={`mode-toggle ${!isManual ? 'active' : ''}`}
              onClick={() => !isManual || toggleManualMode(key)}
              title="Select from list"
            >
              Select
            </button>
            <button
              className={`mode-toggle ${isManual ? 'active' : ''}`}
              onClick={() => isManual || toggleManualMode(key)}
              title="Enter manually"
            >
              <Edit3 size={12} />
              Manual
            </button>
          </div>
          {isManual ? (
            <input
              type="text"
              value={playgroundArgs[key] || ''}
              onChange={(e) => handleArgChange(key, e.target.value || undefined)}
              placeholder="Enter import job ID"
              className="form-input"
            />
          ) : (
            <CustomSelect
              value={playgroundArgs[key] || ''}
              onChange={(value) => handleArgChange(key, value || undefined)}
              options={jobOptions}
              placeholder={loadingImportJobs ? 'Loading...' : importJobs.length === 0 ? 'No import jobs available' : 'Select an import job'}
              disabled={loadingImportJobs}
            />
          )}
        </div>
      );
    }

    // Export ID field - manual entry only (no pre-fetch)
    if (key === 'export_id') {
      return (
        <input
          type="text"
          value={playgroundArgs[key] || ''}
          onChange={(e) => handleArgChange(key, e.target.value || undefined)}
          placeholder="Enter export ID"
          className="form-input"
        />
      );
    }

    // Fallback for other smart fields
    return (
      <input
        type="text"
        value={playgroundArgs[key] || ''}
        onChange={(e) => handleArgChange(key, e.target.value || undefined)}
        placeholder={schema.description || `Enter ${key}`}
        className="form-input"
      />
    );
  };

  const handleSchemaError = (path: string, error?: string) => {
    setSchemaErrors(previous => {
      const next = { ...previous };
      if (error) next[path] = error; else delete next[path];
      return next;
    });
  };

  // Render regular field
  const renderField = (key: string, schema: any) => {
    if (schema.type === 'object' || schema.properties || (schema.type === 'array' && schema.items?.type === 'object')) {
      if (key !== 'files') return <McpSchemaField path={key} schema={schema} value={playgroundArgs[key]} disabled={isExecuting} onChange={value => handleArgChange(key, value)} onError={handleSchemaError} />;
    }
    if (key === 'instruction' || key === 'prompt_instruction') {
      return (
        <textarea
          value={playgroundArgs[key] || ''}
          onChange={(event) => handleArgChange(key, event.target.value || undefined)}
          placeholder={schema.description || `Enter ${key}`}
          rows={4}
          maxLength={schema.maxLength}
          disabled={isExecuting}
          className="form-input"
        />
      );
    }

    if (key === 'files') {
      const videosOnly = false;
      return (
        <div className="mcp-media-attachments">
          <input
            type="file" multiple accept={videosOnly ? LOCAL_VIDEO_FILE_ACCEPT : LOCAL_MEDIA_FILE_ACCEPT} disabled={isExecuting}
            aria-label={videosOnly ? 'Attach videos' : 'Attach media files'}
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              const invalid = files.find(file => videosOnly
                ? classifyUploadMedia(file.name, file.type) !== 'video'
                : !isSupportedLocalMediaFile(file));
              if (invalid) {
                setMediaError(`${videosOnly ? 'Choose a supported video' : 'Unsupported media file'}: ${invalid.name}`);
              } else if (files.some(file => exceedsMediaUploadSizeLimit(file.size))) {
                setMediaError(MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE);
              } else if (files.length > (schema.maxItems || 100)) {
                setMediaError(`Choose up to ${schema.maxItems || 100} media files.`);
              } else {
                handleMediaFilesChange(files.map(file => ({ file, id: crypto.randomUUID() })));
              }
              event.target.value = '';
            }}
          />
          {mediaFiles.length > 0 && <ul>{mediaFiles.map(entry => (
            <li key={entry.id}>
              <span>{entry.file.name}</span>
              <button type="button" disabled={isExecuting} aria-label={`Remove ${entry.file.name}`}
                onClick={() => handleMediaFilesChange(mediaFiles.filter(file => file.id !== entry.id))}>
                <X size={14} />
              </button>
            </li>
          ))}</ul>}
          <span className="param-hint">Choose {videosOnly ? 'videos' : 'media'}, then execute to upload and continue. With no files selected, the workflow waits for your upload.</span>
        </div>
      );
    }
    if (schema.type === 'boolean') {
      return (
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={playgroundArgs[key] || false}
            onChange={(e) => handleArgChange(key, e.target.checked)}
          />
          <span>{schema.description || 'Enable'}</span>
        </label>
      );
    }

    if (schema.type === 'number' || schema.type === 'integer') {
      return (
        <input
          type="number"
          value={playgroundArgs[key] ?? ''}
          onChange={(e) => handleArgChange(key, e.target.value ? Number(e.target.value) : undefined)}
          placeholder={schema.description || `Enter ${key}`}
          min={schema.minimum}
          max={schema.maximum}
          className="form-input"
        />
      );
    }

    if (schema.type === 'array') {
      return (
        <input
          type="text"
          value={playgroundArgs[key]?.join(', ') || ''}
          onChange={(e) => handleArgChange(key, e.target.value ? e.target.value.split(',').map(s => s.trim()) : undefined)}
          placeholder={`${schema.description || key} (comma-separated)`}
          className="form-input"
        />
      );
    }

    if (schema.enum) {
      return (
        <CustomSelect
          value={playgroundArgs[key] || ''}
          onChange={(value) => handleArgChange(key, value || undefined)}
          options={schema.enum.map((opt: string) => ({
            value: opt,
            label: opt
          }))}
          placeholder="Select..."
        />
      );
    }

    return (
      <input
        type="text"
        value={playgroundArgs[key] || ''}
        onChange={(e) => handleArgChange(key, e.target.value || undefined)}
        placeholder={schema.description || `Enter ${key}`}
        className="form-input"
      />
    );
  };

  if (!isOpen || !tool) return null;

  return (
    <div
      ref={modalRef}
      className={`mcp-tool-sidebar ${isOpen ? 'open' : ''}`}
      style={{ width: sidebarWidth }}
    >
      {/* Resize handle */}
      <div
        className="sidebar-resize-handle"
        onMouseDown={handleResizeStart}
      />

      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <Terminal size={20} />
          <h2>{tool.name}</h2>
        </div>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="sidebar-content">
        {/* Tool Description */}
        <div className="tool-description-section">
          <p>{tool.description.split('\n')[0]}</p>
        </div>

        {/* Helper Section - for tools that need video_id/run_id but don't have index_id */}
        {(() => {
          const properties = tool.inputSchema.properties || {};
          const hasVideoId = 'video_id' in properties || 'video_ids' in properties;
          const hasRunId = 'run_id' in properties || 'run_ids' in properties;
          const hasIndexId = 'index_id' in properties;
          const needsHelper = (hasVideoId || hasRunId) && !hasIndexId;

          if (needsHelper) {
            const indexOptions = indexes.map(index => ({
              value: index.index_id,
              label: index.name
            }));

            // Determine what the helper is for
            const helperPurpose = hasVideoId && hasRunId
              ? 'videos and prompt runs'
              : hasVideoId
                ? 'videos'
                : 'prompt runs';

            return (
              <div className="helper-section">
                <div className="helper-header">
                  <span className="helper-badge">Playground Helper</span>
                  <span className="helper-note">Not a tool parameter</span>
                </div>
                <div className="helper-field">
                  <label>Select an index to browse {helperPurpose}</label>
                  <CustomSelect
                    value={helperIndexId}
                    onChange={(value) => setHelperIndexId(value)}
                    options={indexOptions}
                    placeholder={loadingIndexes ? 'Loading...' : 'Select an index'}
                    disabled={loadingIndexes}
                  />
                </div>
              </div>
            );
          }
          return null;
        })()}

        {!!(tool.inputSchema as any).examples?.length && <details className="mcp-examples">
          <summary>Examples and option combinations</summary>
          {(tool.inputSchema as any).examples.map((example: Record<string, any>, index: number) => <div key={index}>
            <pre>{JSON.stringify(example, null, 2)}</pre>
            <button type="button" disabled={isExecuting} onClick={() => { setPlaygroundArgs(example); setSchemaErrors({}); setMediaError(null); }}>Use example {index + 1}</button>
          </div>)}
        </details>}
        {/* Parameters Form */}
        <div className="parameters-section">
          <h3>Parameters</h3>
          {Object.entries(tool.inputSchema.properties || {}).length === 0 ? (
            <p className="no-params">This tool has no parameters.</p>
          ) : (
            <div className="parameters-form">
              {Object.entries(resolveMcpSchema(tool.inputSchema, playgroundArgs).properties || {}).map(([key, schema]: [string, any]) => {
                const isRequired = tool.inputSchema.required?.includes(key) || false;
                const isSmartField = SMART_FIELDS.includes(key);
                const isImageField = IMAGE_FIELDS.includes(key);

                return (
                  <div key={key} className="param-field">
                    <label>
                      {key}
                      {isRequired && <span className="required">*</span>}
                    </label>

                    {isImageField
                      ? renderImageField(key, schema, isRequired)
                      : isSmartField
                        ? renderSmartField(key, schema, isRequired)
                        : renderField(key, schema)}

                    {schema.description && schema.type !== 'boolean' && !isSmartField && !isImageField && (
                      <span className="param-hint">
                        {schema.description}
                        {schema.default !== undefined && ` (default: ${schema.default})`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {Object.values(schemaErrors).map(error => <p role="alert" key={error}>{error}</p>)}
        {mediaError && <div className="image-error" role="alert"><AlertCircle size={14} /><span>{mediaError}</span></div>}
        {uploadProgress && <p role="status">{uploadProgress}</p>}
        {result?.result?._meta?.upload && (
          <p className="param-hint">Attach your media above, then upload and continue this workflow.</p>
        )}

        {/* Result Section */}
        {result && (
          (() => {
            const displayPayload = getMcpPlaygroundDisplayPayload(result);
            const taskRead = getMcpTaskReadRequest(result);
            const resultLink = getMcpAnalysisResultLink(displayPayload);
            return (
          <div className={`result-section ${result.success ? 'success' : 'error'}`}>
            <div className="result-header">
              <div className="result-status">
                {result.success ? (
                  <>
                    <CheckCircle size={16} />
                    <span>Success</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} />
                    <span>Error</span>
                  </>
                )}
              </div>
              <div className="result-meta">
                <Clock size={14} />
                <span>{result.execution_time_ms}ms</span>
                <button
                  className={`copy-result-btn ${resultCopied ? 'copied' : ''}`}
                  onClick={handleCopyResult}
                  title="Copy result"
                >
                  {resultCopied ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <pre className="result-code">
              {JSON.stringify(
                displayPayload ?? null,
                null,
                2
              )}
            </pre>
            {result.success && taskRead && !result.result?._meta?.upload && (
              <button className="url-submit-btn" disabled={isExecuting} onClick={async () => {
                const owner = tool.name;
                executionInFlightRef.current = true;
                setIsExecuting(true);
                try {
                  const response = await apiService.executeMcpPlayground(taskRead.name, taskRead.arguments);
                  if (visibleToolNameRef.current === owner) {
                    if (response.success) setResult(response);
                    else setMediaError('Status refresh failed; check this same task again.');
                  }
                } catch (error) {
                  if (visibleToolNameRef.current === owner) setMediaError(getApiErrorMessage(error, 'Could not refresh workflow status.'));
                } finally {
                  executionInFlightRef.current = false;
                  setIsExecuting(false);
                }
              }}>Check workflow status</button>
            )}
            {result.success && resultLink && (
              <a className="mcp-open-results" href={resultLink}>
                Open results in Studio
              </a>
            )}
          </div>
            );
          })()
        )}
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <button className="cancel-btn" onClick={onClose}>
          Close
        </button>
        <button
          className="execute-btn"
          onClick={handleExecute}
          disabled={isExecuting || Object.keys(schemaErrors).length > 0 || imageState.isLoading || (Boolean(result?.result?._meta?.upload) && mediaFiles.length === 0)}
        >
          {isExecuting ? (
            <>
              <RefreshCw size={16} className="spinning" />
              Executing...
            </>
          ) : (
            <>
              <Play size={16} />
              {result?.result?._meta?.upload ? 'Upload and continue' : 'Execute Tool'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default McpToolSidebar;
